import React, { useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Typography,
  Card,
  Space,
  Tooltip,
  message,
  InputNumber,
  Tag,
  Input,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";

const { Text } = Typography;

const BoxesSection = ({ boxState, updateBoxState }) => {
  const { prefix, startRange, endRange, manual, boxes } = boxState;

  // Generate Boxes
  const handleGenerateBoxes = () => {
    if (
      !startRange ||
      !endRange ||
      isNaN(startRange) ||
      isNaN(endRange) ||
      Number(startRange) < 0 ||
      Number(endRange) < 0
    ) {
      message.error("Please enter valid box numbers");
      return;
    }
    if (String(startRange).length > 5 || String(endRange).length > 5) {
      message.error("Maximum 5 digits are allowed");
      return;
    }
    const start = parseInt(startRange, 10);
    const end = parseInt(endRange, 10);
    if (start > end) {
      message.error("Start should be less than end");
      return;
    }
    if (end - start > 99) {
      message.error("Maximum 100 Boxes can be generated at a time");
      return;
    }
    const newBoxes = [];
    for (let i = start; i <= end; i++) {
      const numericPart = String(i).padStart(5, "0");
      const box = prefix.trim() ? `${prefix}-${numericPart}` : numericPart;
      if (!boxes.includes(box)) {
        newBoxes.push(box);
      }
    }
    if (newBoxes?.length === 0) {
      message.info("No new unique boxes to add");
      return;
    }
    updateBoxState({ boxes: [...boxes, ...newBoxes] });
  };

  // Add manual box
  const handleAddManualBox = () => {
    if (!manual || isNaN(manual) || Number(manual) < 0) {
      message.error("Please enter valid box numbers");
      return;
    }
    if (String(manual).length > 5) {
      message.error("Maximum 5 digits are allowed");
      return;
    }
    const numericPart = String(manual).padStart(5, "0");
    const newBox = prefix.trim() ? `${prefix}-${numericPart}` : numericPart;
    if (boxes.includes(newBox)) {
      message.warning("Box No. already exists");
      return;
    }
    updateBoxState({ boxes: [...boxes, newBox], manual: "" });
  };

  // Delete box
  const handleDeleteBox = (box) => {
    updateBoxState({ boxes: boxes.filter((s) => s !== box) });
  };

  useEffect(() => {
    if (boxes.length > 0) {
      const updatedBoxes = boxes.map((box) => {
        const parts = box.split("-");
        const numericPart = parts[parts.length - 1];
        return prefix.trim() ? `${prefix}-${numericPart}` : numericPart;
      });
      updateBoxState({ boxes: updatedBoxes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix]);

  return (
    <Card
      style={{
        borderRadius: 12,
        border: "1px solid #E5E8EC",
        background: "#fff",
        marginBottom: 16,
      }}
    >
      <Row
        justify="space-between"
        align="middle"
        style={{
          borderBottom: "2px solid #23235F",
          marginBottom: 16,
          paddingBottom: 12,
        }}
      >
        <Text style={{ fontSize: 18, fontWeight: 700, color: "#23235F" }}>
          📦 Boxes
        </Text>
        <Space align="center">
          <Text style={{ fontWeight: 600, color: "#232A36", fontSize: 13 }}>
            Prefix
          </Text>
          <Input
            value={prefix}
            onChange={(e) => updateBoxState({ prefix: e.target.value })}
            placeholder="e.g., AG-BX"
            style={{ borderRadius: 8, fontWeight: 600, width: 120 }}
          />
        </Space>
      </Row>

      {/* Box Range */}
      <Text style={{ fontWeight: 600, color: "#232A36" }}>Box Range</Text>
      <Row gutter={8} style={{ marginTop: 6, marginBottom: 16 }}>
        <Col span={9}>
          <InputNumber
            value={startRange}
            onChange={(value) => updateBoxState({ startRange: value })}
            placeholder="Start"
            style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
          />
        </Col>
        <Col span={9}>
          <InputNumber
            value={endRange}
            onChange={(value) => updateBoxState({ endRange: value })}
            placeholder="End"
            style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
          />
        </Col>
        <Col span={6}>
          <Button
            type="primary"
            onClick={handleGenerateBoxes}
            style={{
              width: "100%",
              borderRadius: 8,
              fontWeight: 700,
              background: "#23235F",
            }}
          >
            Generate Boxes
          </Button>
        </Col>
      </Row>

      {/* Add Manual Box */}
      <Text style={{ fontWeight: 600, color: "#232A36" }}>Add Manual Box</Text>
      <Row gutter={12} style={{ marginTop: 6, marginBottom: 15 }}>
        <Col span={18}>
          <InputNumber
            value={manual}
            onChange={(value) => updateBoxState({ manual: value })}
            placeholder="Enter Numeric Value (e.g., 1)"
            style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
          />
        </Col>
        <Col span={6}>
          <Button
            type="primary"
            onClick={handleAddManualBox}
            style={{
              width: "100%",
              borderRadius: 8,
              fontWeight: 700,
              background: "#23235F",
            }}
          >
            Add Box
          </Button>
        </Col>
      </Row>

      {/* Box IDs */}
      {boxes?.length ? (
        <Card
          style={{
            borderRadius: 15,
            border: "1px solid #E5E8EC",
            background: "#FAFAFA",
          }}
        >
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 12 }}
          >
            <Text style={{ fontWeight: 700, fontSize: 16, color: "#232A36" }}>
              Box IDs
            </Text>
            <Tag
              style={{
                fontWeight: 700,
                fontSize: 15,
                background: "#E5E8EC",
                borderRadius: 6,
                padding: "4px 12px",
                border: "none",
              }}
            >
              {boxes.length} {`Box${boxes.length > 1 ? "es" : ""}`}
            </Tag>
          </Row>
          <Row
            gutter={[12, 12]}
            style={{
              border: "1px solid #E5E8EC",
              padding: "12px",
              borderRadius: 12,
              overflow: "auto",
              maxHeight: 220,
            }}
          >
            {boxes.map((box, idx) => (
              <Col span={8} key={box + idx}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "#fff",
                    borderRadius: 8,
                    border: "1px solid #E5E8EC",
                    padding: "4px 12px",
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#232A36",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <span>{box}</span>
                  <Tooltip title="Delete">
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteBox(box)}
                      style={{ color: "#E14D4F", fontSize: 16 }}
                    />
                  </Tooltip>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      ) : null}
    </Card>
  );
};

export default BoxesSection;
